import type { CompanyValues } from '../../types/companyValues'
import type { ZervantSettings } from '../../types/zervantSettings'
import type { FieldInterface, ZervantSettingsModel } from '../../types/zervantSettingsModel'
import type { ModelDocumentType } from '../../types/zervantSettingsModelDocumentType'
import type { ZervantSettingsValues } from '../../types/zervantSettingsValues'
import type { InvoicingSettingsFieldPatch } from '../types/invoicingSettingsFieldPatch'
import { type InvoicingSettingsPatchableModelType, patchableModelTypes } from '../types/invoicingSettingsModelPatches'
import type { InvoicingSettingsValuesPatch } from '../types/invoicingSettingsValuesPatch'
import type { UpdateInvoicingSettingsPatch } from '../types/updateInvoicingSettingsPatch'

interface GetInvoicingSettingsPayloadOptions {
  cachedSettings: ZervantSettings
  patch: UpdateInvoicingSettingsPatch
}

// Fold a module's patch into the latest cached settings document. The endpoint accepts only a
// full document, so a later save must not resend a stale copy of another module's fields.
export const getInvoicingSettingsPayload = (options: GetInvoicingSettingsPayloadOptions): ZervantSettings => {
  const { cachedSettings, patch } = options

  return {
    ...cachedSettings,
    models: getPatchedModels(cachedSettings.models, patch),
    values: getPatchedValues(cachedSettings.values, patch.values),
  }
}

const getPatchedModels = (
  models: ZervantSettingsModel[],
  patch: UpdateInvoicingSettingsPatch,
): ZervantSettingsModel[] => {
  if (!patch.modelPatches) {
    return models
  }

  const { modelPatches } = patch
  const patchedModelTypes = Object.keys(modelPatches) as InvoicingSettingsPatchableModelType[]

  // Fail loudly: skipping a missing type (e.g. `estimate` on a two-model write) would look
  // successful while leaving that document type on the old field values.
  for (const modelType of patchedModelTypes) {
    if (isModelTypeMissing(models, modelType)) {
      throw new Error(`Invoicing settings cache is missing model type "${modelType}"`)
    }
  }

  return models.map((model) => {
    const fieldPatch = isPatchableModelType(model.type) ? modelPatches[model.type] : undefined
    if (!fieldPatch) {
      return model
    }

    // GET always hydrates default fields; a missing name here is a corrupt cache, not a first-write
    // create. Skipping would look successful while leaving the field on the old value.
    const missingFieldName = getMissingPatchedFieldName(model, fieldPatch)
    if (missingFieldName) {
      throw new Error(`Invoicing settings cache is missing field "${missingFieldName}" on model type "${model.type}"`)
    }

    return {
      ...model,
      fields: model.fields.map((field) => getPatchedField(field, fieldPatch[field.name])),
    }
  })
}

const isPatchableModelType = (modelType: ModelDocumentType): modelType is InvoicingSettingsPatchableModelType =>
  (patchableModelTypes as readonly ModelDocumentType[]).includes(modelType)

const isModelTypeMissing = (models: ZervantSettingsModel[], modelType: InvoicingSettingsPatchableModelType): boolean =>
  !models.some((model) => model.type === modelType)

const getMissingPatchedFieldName = (
  model: ZervantSettingsModel,
  fieldPatch: InvoicingSettingsFieldPatch,
): string | undefined =>
  Object.keys(fieldPatch).find((fieldName) => !model.fields.some((field) => field.name === fieldName))

const getPatchedField = (
  field: FieldInterface,
  fieldPatch: InvoicingSettingsFieldPatch[keyof InvoicingSettingsFieldPatch],
): FieldInterface => {
  if (!fieldPatch) {
    return field
  }

  return { ...field, ...fieldPatch }
}

const getPatchedValues = (
  cachedValues: ZervantSettingsValues,
  valuesPatch?: InvoicingSettingsValuesPatch,
): ZervantSettingsValues => {
  if (!valuesPatch) {
    return cachedValues
  }

  return {
    ...cachedValues,
    ...valuesPatch,
    // Re-merge after the spread: a partial `company` / `regional` would otherwise replace the
    // whole object and drop keys the module did not send. Arrays such as `paymentMethods` stay
    // replaced wholesale from `...valuesPatch`.
    company: valuesPatch.company ? getPatchedCompany(cachedValues.company, valuesPatch.company) : cachedValues.company,
    regional: valuesPatch.regional ? { ...cachedValues.regional, ...valuesPatch.regional } : cachedValues.regional,
  }
}

const getPatchedCompany = (cachedCompany: CompanyValues, companyPatch: Partial<CompanyValues>): CompanyValues => {
  return {
    ...cachedCompany,
    ...companyPatch,
    // These two hold sibling keys owned by different modules, so a partial patch must not
    // replace the whole object. Arrays such as `tradeNames` stay replaced wholesale above.
    legalFormSettings: companyPatch.legalFormSettings
      ? { ...cachedCompany.legalFormSettings, ...companyPatch.legalFormSettings }
      : cachedCompany.legalFormSettings,
    legalMentions: companyPatch.legalMentions
      ? { custom: { ...cachedCompany.legalMentions?.custom, ...companyPatch.legalMentions.custom } }
      : cachedCompany.legalMentions,
  }
}
